import type {FilterParam} from '@/modules/common/context/QueryParams/models';
import type {UseFilterParams} from '@/modules/common/hooks/useFilterParams';
import type {RoomsType} from '@/modules/common/models/rooms';
import type {
  Inventory,
  INVENTORY_CATEGORY_ROOM_VIEW,
  InventoryCategory,
  InventoryCategoryType,
  Price,
} from '@/redux/v1/models';

import {sortBy} from 'lodash';
import {useTranslation} from 'react-i18next';

import {config} from '@/config';
import {collapseRoomAttributesFilterValue} from '@/modules/common/hooks/filter/utils/collapseRoomAttributesFilterValue';
import {customFilterSorting} from '@/modules/common/hooks/filter/utils/customFilterSorting';
import {
  FILTER_AIRLINES,
  FILTER_BOARD_TYPE,
  FILTER_DESTINATION_AIRPORTS,
  FILTER_DURATION,
  FILTER_FLIGHT_TRANSFER,
  FILTER_ROOM_ATTRIBUTES,
  FILTER_ROOM_TYPE,
  FILTER_TOUR_OPERATOR,
} from '@/modules/common/models/filters';
import {getDisplayPrice} from '@/modules/common/utils/price';
import {
  INVENTORY_CATEGORY_AIRLINE,
  INVENTORY_CATEGORY_BOARD_TYPE,
  INVENTORY_CATEGORY_DESTINATION_AIRPORT,
  INVENTORY_CATEGORY_DURATION,
  INVENTORY_CATEGORY_ROOM_ATTRIBUTES,
  INVENTORY_CATEGORY_ROOM_TYPE,
  INVENTORY_CATEGORY_TOUR_OPERATOR,
  INVENTORY_CATEGORY_TRANSFER,
} from '@/redux/v1/models';
import {isRoomAttributeSwimUpPoolType} from '@/utils/inviaType';

type Props = {
  filterParams: UseFilterParams;
  inventoryUnfiltered?: null | Inventory;
  inventoryFiltered?: null | Inventory;
  rooms?: RoomsType;
};

type ValidInventoryCategoryType = Exclude<
  InventoryCategoryType,
  // category is deprecated, we use roomAttributes instead
  typeof INVENTORY_CATEGORY_ROOM_VIEW
>;

const filterIdByInventoryCategory = {
  [INVENTORY_CATEGORY_BOARD_TYPE]: FILTER_BOARD_TYPE,
  [INVENTORY_CATEGORY_ROOM_TYPE]: FILTER_ROOM_TYPE,
  [INVENTORY_CATEGORY_ROOM_ATTRIBUTES]: FILTER_ROOM_ATTRIBUTES,
  [INVENTORY_CATEGORY_TOUR_OPERATOR]: FILTER_TOUR_OPERATOR,
  [INVENTORY_CATEGORY_DURATION]: FILTER_DURATION,
  [INVENTORY_CATEGORY_TRANSFER]: FILTER_FLIGHT_TRANSFER,
  [INVENTORY_CATEGORY_AIRLINE]: FILTER_AIRLINES,
  [INVENTORY_CATEGORY_DESTINATION_AIRPORT]: FILTER_DESTINATION_AIRPORTS,
};

const filterParamByInventoryCategory: Record<
  ValidInventoryCategoryType,
  FilterParam
> = {
  [INVENTORY_CATEGORY_BOARD_TYPE]: 'boardTypes',
  [INVENTORY_CATEGORY_ROOM_TYPE]: 'roomTypes',
  [INVENTORY_CATEGORY_ROOM_ATTRIBUTES]: 'roomAttributes',
  [INVENTORY_CATEGORY_TOUR_OPERATOR]: 'tourOperators',
  [INVENTORY_CATEGORY_DURATION]: 'duration',
  [INVENTORY_CATEGORY_TRANSFER]: 'transportations',
  [INVENTORY_CATEGORY_AIRLINE]: 'airlines',
  [INVENTORY_CATEGORY_DESTINATION_AIRPORT]: 'arrivalAirports',
};

export const useInventoryFilters = ({
  filterParams: [filterParams, setFilterParams],
  inventoryUnfiltered,
  inventoryFiltered,
  rooms,
}: Props) => {
  const {t} = useTranslation(['general', 'filters']);

  const inventoryCategoryToFilter = <Id extends ValidInventoryCategoryType>({
    id,
    param,
    inventoryCategoryUnfiltered,
    inventoryCategoryFiltered,
  }: {
    id: (typeof filterIdByInventoryCategory)[Id];
    param: FilterParam;
    inventoryCategoryUnfiltered: InventoryCategory | undefined;
    inventoryCategoryFiltered: InventoryCategory | undefined;
  }) => {
    let value = (filterParams[param] ?? []) as Array<string>;
    // @FIXME should we do this during query parameter parsing already?
    if (id === FILTER_ROOM_ATTRIBUTES) {
      value = collapseRoomAttributesFilterValue(value);
    }
    const options = customFilterSorting(
      (inventoryCategoryUnfiltered?.variations || []).map(
        (variationUnfiltered) => {
          const matchingFilteredVariation =
            inventoryCategoryFiltered?.variations?.find(
              (variationFiltered) =>
                variationFiltered.value === variationUnfiltered.value
            );

          return {
            value: variationUnfiltered.value,
            label: variationUnfiltered.translation?.trim() || '', // @TODO already sanitize in action?
            priceMin: getDisplayPrice(
              matchingFilteredVariation?.priceDisplay,
              rooms
            ) as Price | undefined,
            active: value.includes(variationUnfiltered.value),
            available: matchingFilteredVariation != null,
            description: isRoomAttributeSwimUpPoolType(
              variationUnfiltered.value
            )
              ? t('filters:roomAttributes.options.balconyTerrace.description')
              : '',
          };
        }
      ),
      id
    );

    return {
      id,
      title: inventoryCategoryUnfiltered?.translation || '',
      active: value.length > 0,
      available: options.length > 0,
      value,
      options,
      setValue: (nextValue: Array<string>) => {
        setFilterParams((previous) => ({...previous, [param]: nextValue}));
      },
      clear: () => {
        setFilterParams((previous) => ({...previous, [param]: null}));
      },
    };
  };

  const durationCategoryToFilter = ({
    id,
    inventoryCategoryUnfiltered,
    inventoryCategoryFiltered,
  }: {
    id: string;
    inventoryCategoryUnfiltered: InventoryCategory | undefined;
    inventoryCategoryFiltered: InventoryCategory | undefined;
  }) => {
    const value = filterParams.duration as null | undefined | number;
    const options = sortBy(
      (inventoryCategoryUnfiltered?.variations || []).map(
        (variationUnfiltered) => {
          const matchingFilteredVariation =
            inventoryCategoryFiltered?.variations?.find(
              (variationFiltered) =>
                variationFiltered.value === variationUnfiltered.value
            );

          return {
            value: variationUnfiltered.value,
            label: t('general:xDays', {
              count: Number(variationUnfiltered.value),
            }),
            priceMin: getDisplayPrice(
              matchingFilteredVariation?.priceDisplay,
              rooms
            ),
            active: variationUnfiltered.value === String(value),
            available: matchingFilteredVariation != null,
          };
        }
      ),
      (option) => Number(option.value)
    );

    return {
      id,
      title: inventoryCategoryUnfiltered?.translation || '',
      active: value != null,
      available: options.length > 0,
      value,
      options,
      setValue: (nextValue: string) => {
        setFilterParams((previous) => ({
          ...previous,
          duration: Number(nextValue),
        }));
      },
      clear: () => {
        setFilterParams((previous) => ({...previous, duration: null}));
      },
    };
  };

  const transferCategoryToFilter = ({id}: {id: string}) => {
    const value = (filterParams.transportations ?? []) as Array<string>;
    // Static values used due to limited support by suppliers atm.
    const options = config.filters.transfer.options.map(
      ([filterId, option]) => ({
        value: option,
        label: t(`filters:flightTransfer.${filterId}`),
        description: t(`filters:tooltips.${filterId}`),
        active: value.includes(option),
        available: true,
      })
    );

    return {
      id,
      title: t('filters:headlines.flightTransfer'),
      value,
      active: value.length > 0,
      available: options.length > 0,
      options,
      setValue: (nextValue: Array<string>) => {
        setFilterParams((previous) => ({
          ...previous,
          transportations: nextValue,
        }));
      },
      clear: () => {
        setFilterParams((previous) => ({...previous, transportations: null}));
      },
    };
  };

  // See comment in useMetaFilters on why we do this
  const inventoryUnfilteredMerged: Array<InventoryCategory> = [
    INVENTORY_CATEGORY_BOARD_TYPE,
    INVENTORY_CATEGORY_ROOM_TYPE,
    INVENTORY_CATEGORY_ROOM_ATTRIBUTES,
    INVENTORY_CATEGORY_TOUR_OPERATOR,
    INVENTORY_CATEGORY_DURATION,
  ].map((name) => {
    const existingInventoryCategory = inventoryUnfiltered?.find(
      (inventoryCategory) => inventoryCategory.name === name
    );
    return (
      existingInventoryCategory || {
        name,
        translation: '',
        variations: [],
      }
    );
  });

  return [
    // eslint-disable-next-line sonarjs/function-return-type
    ...inventoryUnfilteredMerged.flatMap((entryUnfiltered) => {
      const id = entryUnfiltered.name;
      const entryFiltered = inventoryFiltered?.find(
        (entry) => entry.name === id
      );

      switch (id) {
        case INVENTORY_CATEGORY_BOARD_TYPE:
        case INVENTORY_CATEGORY_ROOM_TYPE:
        case INVENTORY_CATEGORY_ROOM_ATTRIBUTES:
        case INVENTORY_CATEGORY_TOUR_OPERATOR: {
          return inventoryCategoryToFilter<
            | typeof INVENTORY_CATEGORY_BOARD_TYPE
            | typeof INVENTORY_CATEGORY_ROOM_TYPE
            | typeof INVENTORY_CATEGORY_TOUR_OPERATOR
            | typeof INVENTORY_CATEGORY_ROOM_ATTRIBUTES
          >({
            id: filterIdByInventoryCategory[id],
            param: filterParamByInventoryCategory[id],
            inventoryCategoryUnfiltered: entryUnfiltered,
            inventoryCategoryFiltered: entryFiltered,
          });
        }
        case INVENTORY_CATEGORY_DURATION: {
          return durationCategoryToFilter({
            id: FILTER_DURATION,
            inventoryCategoryUnfiltered: entryUnfiltered,
            inventoryCategoryFiltered: entryFiltered,
          });
        }
        // case INVENTORY_CATEGORY_TRANSFER:
        //   return transferCategoryToFilter({
        //     id: FILTER_FLIGHT_TRANSFER,
        //     inventoryCategoryUnfiltered: entryUnfiltered,
        //     inventoryCategoryFiltered: entryFiltered,
        //   });
        default: {
          return [];
        }
      }
    }),
    // Options are static atm. as supplier support is limited.
    // In principle though, the filter is still part of the inventory.
    transferCategoryToFilter({
      id: FILTER_FLIGHT_TRANSFER,
    }),
  ];
};
